import { supabase } from './supabaseClient';

/**
 * Authorization helpers for property-scoped service calls.
 *
 * Services that accept a `propertyId` argument from the caller must verify
 * the authenticated user is assigned to that property via user_properties.
 * RLS enforces this server-side, but explicit app-layer checks provide
 * defense in depth and fail closed if an RLS policy is ever loosened.
 */

export class PropertyAuthorizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PropertyAuthorizationError';
  }
}

/**
 * Return the authenticated user's id. Verifies the JWT against the auth
 * server rather than trusting any caller-supplied id.
 */
export async function requireAuthenticatedUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user?.id) {
    throw new PropertyAuthorizationError(
      'Authentication required: no active session was found for this request.'
    );
  }
  return data.user.id;
}

/**
 * Load the set of property ids the authenticated user is assigned to in
 * user_properties. Callers can use this to intersect with lookup results
 * and avoid leaking property data from other tenants.
 */
export async function getOwnedPropertyIds(
  userId: string
): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('user_properties')
    .select('property_id')
    .eq('user_id', userId);

  if (error) {
    throw new PropertyAuthorizationError(
      `Failed to load property assignments: ${error.message}`
    );
  }
  return new Set((data ?? []).map((row) => row.property_id));
}

/**
 * Throw unless the authenticated user is assigned to `propertyId`.
 * Call this at the top of any service method that accepts a propertyId
 * and queries property-scoped tables (properties, pms_integrations,
 * room_types, calendar_data, reservations, etc.).
 */
export async function assertPropertyAccess(propertyId: string): Promise<void> {
  const userId = await requireAuthenticatedUserId();
  const { data, error } = await supabase
    .from('user_properties')
    .select('property_id')
    .eq('user_id', userId)
    .eq('property_id', propertyId)
    .maybeSingle();

  if (error) {
    throw new PropertyAuthorizationError(
      `Failed to verify property access: ${error.message}`
    );
  }
  if (!data) {
    throw new PropertyAuthorizationError(
      `Access denied: you are not assigned to property "${propertyId}".`
    );
  }
}
